/*
 * PDP variant availability — hide option values that have no variant.
 *
 * THE BUG THIS FIXES
 * Shopify's options are independent lists. Flip Traveler declares 7 colours and
 * 2 sizes, so the picker offers 7 x 2 = 14 combinations, but only 8 have a
 * variant (12oz: Black/White/Basal Grey, 16oz: Black/Evergreen/SandStone/Home
 * Blue/Thousand Hills Pink). Picking 12oz + Home Blue is reachable and produces
 * NO variant:
 *
 *   - the hidden input[name="id"] keeps the last valid variant
 *   - the price keeps showing that variant's price
 *   - the buy button stays enabled
 *   - nothing says "unavailable"
 *
 * so the page silently shows one thing and would order another. The options are
 * correlated, so the invalid pairs are exactly the six that cross the sizes.
 *
 * WHAT IT DOES
 * A colour is shown only if some variant carries it together with the style and
 * size currently selected; colours with no such variant are hidden, so an
 * invalid pair cannot be chosen at all.
 *
 * Only the colour option is filtered. Filtering size too would create dead
 * ends: at 16oz + Home Blue every 12oz colour is invalid, so 12oz would
 * disappear and the customer could never get back to it. Sizes therefore stay
 * visible and the colour adapts instead — when a style/size change leaves the
 * current colour without a variant, the first valid colour is selected and a
 * `change` event is dispatched so the theme re-prices, re-syncs the hidden id
 * and updates the URL through its normal path.
 *
 * Loaded with defer by the MC product template only.
 */
(function () {
  'use strict';

  var picker = document.querySelector('variant-radios');
  if (!picker) return;

  var HIDDEN_ATTR = 'data-pdp-option-hidden';

  function allVariants() {
    var map = window.productVariants || {};
    var list = [];
    for (var key in map) {
      if (!Object.prototype.hasOwnProperty.call(map, key)) continue;
      var variant = map[key];
      if (variant && Array.isArray(variant.options)) list.push(variant);
    }
    return list;
  }

  function fieldsets() {
    return Array.prototype.slice.call(picker.querySelectorAll('fieldset'));
  }

  function radiosOf(fieldset) {
    return Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"]'));
  }

  function labelOf(fieldset, radio) {
    if (!radio.id) return null;
    return fieldset.querySelector('label[for="' + radio.id + '"]');
  }

  function checkedRadio(fieldset) {
    return fieldset.querySelector('input[type="radio"]:checked');
  }

  function currentSelection() {
    return fieldsets().map(function (fieldset) {
      var checked = checkedRadio(fieldset);
      return checked ? checked.value : null;
    });
  }

  /** Does this variant match the selection on every position we know about? */
  function matches(variant, selection) {
    if (!variant.options || variant.options.length !== selection.length) return false;
    for (var i = 0; i < selection.length; i++) {
      if (selection[i] !== null && selection[i] !== variant.options[i]) return false;
    }
    return true;
  }

  /** Is there a variant for this selection with the colour replaced? */
  function colourExists(variants, selection, colourIndex, value) {
    var candidate = selection.slice();
    candidate[colourIndex] = value;
    return variants.some(function (variant) {
      return matches(variant, candidate);
    });
  }

  /*
   * Which fieldset is the colour one? Detected from the rendered swatch classes
   * rather than from option names, which vary per product ("Color", "Colour",
   * "Colour way", ...).
   */
  function colourFieldsetIndex() {
    var fss = fieldsets();
    for (var i = 0; i < fss.length; i++) {
      if (fss[i].querySelector('.color-swatch, .color-image-button')) return i;
    }
    return -1;
  }

  function setHidden(radio, fieldset, shouldHide) {
    var target = labelOf(fieldset, radio) || radio;

    if (shouldHide) {
      target.setAttribute(HIDDEN_ATTR, 'true');
      // Disabled as well as hidden: hidden alone still leaves it focusable and
      // reachable with the keyboard.
      radio.disabled = true;
    } else {
      target.removeAttribute(HIDDEN_ATTR);
      radio.disabled = false;
    }
  }

  /*
   * Keep the visual "selected" ring in step with the checked radios.
   *
   * The theme only ever adds .selected from click handlers, so any programmatic
   * change - the style picker, or our own auto-correct - leaves the ring on the
   * old swatch while the radio has already moved. Mirroring it here is
   * idempotent and cannot fight the click path, which sets the same class.
   */
  function syncSelectedLabels() {
    fieldsets().forEach(function (fieldset) {
      radiosOf(fieldset).forEach(function (radio) {
        var label = labelOf(fieldset, radio);
        if (!label) return;

        var isSelectable =
          label.classList.contains('color-swatch') ||
          label.classList.contains('color-image-button') ||
          label.classList.contains('size-button') ||
          label.classList.contains('variant-button');
        if (!isSelectable) return;

        label.classList.toggle('selected', radio.checked);
      });
    });
  }

  function refresh(correctColour) {
    var variants = allVariants();
    if (!variants.length) return;

    var colourIndex = colourFieldsetIndex();
    if (colourIndex === -1) return;

    var fields = fieldsets();
    var colourFieldset = fields[colourIndex];
    var selection = currentSelection();

    if (selection[colourIndex] === null) return;

    var firstValidRadio = null;

    radiosOf(colourFieldset).forEach(function (radio) {
      var exists = colourExists(variants, selection, colourIndex, radio.value);
      setHidden(radio, colourFieldset, !exists);
      if (exists && !firstValidRadio) firstValidRadio = radio;
    });

    if (!correctColour || !firstValidRadio) {
      syncSelectedLabels();
      return;
    }

    /*
     * The selected colour may not exist for the style/size now selected. Move
     * to the first colour that does. This is what keeps the size picker usable:
     * the customer changes size, and the colour follows it.
     */
    var selected = checkedRadio(colourFieldset);
    var selectedValid = selected && colourExists(variants, selection, colourIndex, selected.value);
    if (selectedValid) {
      syncSelectedLabels();
      return;
    }

    setHidden(firstValidRadio, colourFieldset, false);
    firstValidRadio.checked = true;
    firstValidRadio.dispatchEvent(new Event('change', { bubbles: true }));
    syncSelectedLabels();
  }

  function onPickerChange() {
    // Defer so the theme's VariantRadios handler has already settled state.
    window.setTimeout(function () {
      refresh(true);
    }, 0);
  }

  function start() {
    // Correct nothing on load: the variant in the URL / rendered markup is a
    // real one, so the initial selection is valid by definition.
    refresh(false);
    picker.addEventListener('change', onPickerChange);

    // The theme announces a resolved change with this event; the style picker
    // uses it too, because its programmatic radio writes fire no native change.
    document.addEventListener('variant:changed', function () {
      window.setTimeout(function () {
        refresh(true);
      }, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
